import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { USER_ROLES, type User, type UserRole } from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import { ROLE_LABEL, absoluteTime } from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { useSession } from '../lib/session.tsx';
import { Dialog } from '../components/Dialog.tsx';
import {
  Button,
  ErrorState,
  Field,
  Input,
  ListSkeleton,
  Panel,
  Select,
  Td,
  Th,
  cx,
} from '../components/ui.tsx';

const ROLE_HINT: Record<UserRole, string> = {
  admin: 'Verwaltet Benutzer, Quellen, Regeln und alle Kanäle.',
  operator: 'Quittiert Alarme, pflegt Quellen und Regeln, verwaltet eigene Kanäle.',
  viewer: 'Liest mit, ändert nichts.',
};

export function UsersPage() {
  const { user: current, can } = useSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users.list({ limit: 100 }) });

  const remove = useMutation({
    mutationFn: (id: string) => api.users.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (error) => alert(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen'),
  });

  return (
    <>
      <PageHeader
        title="Benutzer"
        description="Wer sich anmelden darf und was er anfassen kann. Kanäle gehören immer einem Benutzer, Regeln zielen auf Benutzer."
        action={
          can('admin') ? (
            <Button variant="primary" icon={<PlusIcon size={15} />} onClick={() => setCreating(true)}>
              Benutzer anlegen
            </Button>
          ) : null
        }
      />

      <Panel bodyClassName="p-0">
        {users.isLoading ? (
          <ListSkeleton rows={3} />
        ) : users.isError ? (
          <ErrorState message="Die Benutzer konnten nicht geladen werden." onRetry={() => users.refetch()} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[42rem] border-collapse">
              <thead className="border-b border-line bg-sunken">
                <tr>
                  <Th>Name</Th>
                  <Th>Rolle</Th>
                  <Th>Status</Th>
                  <Th>Angelegt</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {(users.data?.items ?? []).map((user) => (
                  <tr key={user.id} className="transition-colors hover:bg-hover">
                    <Td>
                      <span className="block text-[13px] font-medium text-ink">
                        {user.name}
                        {user.id === current?.id ? (
                          <span className="ml-2 text-[11px] font-normal text-ink-3">du</span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 block text-[12px] text-ink-3">{user.email}</span>
                    </Td>
                    <Td>
                      <span className="text-ink">{ROLE_LABEL[user.role]}</span>
                    </Td>
                    <Td>
                      <span className={cx(user.is_active ? 'text-ink-2' : 'text-ink-3')}>
                        {user.is_active ? 'Aktiv' : 'Deaktiviert'}
                      </span>
                    </Td>
                    <Td>{absoluteTime(user.created_at)}</Td>
                    <Td className="text-right">
                      {can('admin') || user.id === current?.id ? (
                        <span className="flex justify-end gap-1.5">
                          <Button size="sm" onClick={() => setEditing(user)}>
                            Bearbeiten
                          </Button>
                          {can('admin') && user.id !== current?.id ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`${user.name} löschen`}
                              onClick={() => {
                                if (confirm(`Benutzer "${user.name}" wirklich löschen?`)) {
                                  remove.mutate(user.id);
                                }
                              }}
                            >
                              <TrashIcon size={14} />
                            </Button>
                          ) : null}
                        </span>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {creating ? (
        <UserDialog
          user={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['users'] });
          }}
        />
      ) : null}

      {editing ? (
        <UserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['users'] });
            void queryClient.invalidateQueries({ queryKey: ['session'] });
          }}
        />
      ) : null}
    </>
  );
}

function UserDialog({
  user,
  onClose,
  onSaved,
}: {
  user: User | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user: current, can } = useSession();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<UserRole>(user?.role ?? 'operator');
  const [password, setPassword] = useState('');
  const [isActive, setIsActive] = useState(user?.is_active ?? true);

  const editingSelf = user !== null && user.id === current?.id;

  const save = useMutation({
    mutationFn: () => {
      if (!user) return api.users.create({ name, email, role, password });
      return api.users.update(user.id, {
        name,
        email,
        ...(can('admin') ? { role, is_active: isActive } : {}),
        ...(password ? { password } : {}),
      });
    },
    onSuccess: onSaved,
  });

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors() : {};
  const generalError =
    save.error instanceof ApiError && Object.keys(fieldErrors).length === 0 ? save.error.message : null;

  return (
    <Dialog title={user ? 'Benutzer bearbeiten' : 'Benutzer anlegen'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" error={fieldErrors.name}>
          {(id) => (
            <Input id={id} required value={name} onChange={(e) => setName(e.target.value)} />
          )}
        </Field>

        <Field label="E-Mail" error={fieldErrors.email}>
          {(id) => (
            <Input
              id={id}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        {can('admin') ? (
          <Field label="Rolle" hint={ROLE_HINT[role]} error={fieldErrors.role}>
            {(id) => (
              <Select
                id={id}
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                disabled={editingSelf}
              >
                {USER_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        <Field
          label={user ? 'Neues Passwort' : 'Passwort'}
          hint={user ? 'Leer lassen, um das bestehende zu behalten.' : 'Mindestens 12 Zeichen.'}
          error={fieldErrors.password}
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              required={!user}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        {user && can('admin') && !editingSelf ? (
          <Field label="Status">
            {(id) => (
              <Select
                id={id}
                value={isActive ? 'active' : 'inactive'}
                onChange={(e) => setIsActive(e.target.value === 'active')}
              >
                <option value="active">Aktiv</option>
                <option value="inactive">Deaktiviert</option>
              </Select>
            )}
          </Field>
        ) : null}

        {generalError ? (
          <p role="alert" className="text-[13px] text-critical">
            {generalError}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Wird gespeichert' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
